import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import Common from "../../../Components/Layouts/Common.js";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from "@mui/material";
import showToast from "../../../Helpers/toast.js";
import TemplateCardView from "../../../Components/ModuleComponents/KIIQuestionnaire/TemplateCardView.js";
import { handleApiError } from "../../../Helpers/ErrorHandler.js";
import PaginationRound from "../../../Components/Table/Material/Pagination.js";
import useApi from "../../../Hooks/useApi.js";
import { usePagination } from "../../../Hooks/usePagination.js";

const SelectQuestionnaireMethod = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState();
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [fileSuccess, setFileSuccess] = useState("");
  const [activeTab, setActiveTab] = useState(location?.state?.activeTab ?? 1);
  const { request ,loading} = useApi();

  const paginateProps = usePagination();
  const {
    page,
    totalData,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    setTotalData,
  } = paginateProps;

  // File drop handling
  const onDrop = (acceptedFiles) => {
    handleFile(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    multiple: false,
  });

  // Validate and set file
  const handleFile = (selectedFile) => {
    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    if (validTypes.includes(selectedFile.type)) {
      setFile(selectedFile);
      setFileError("");
      setFileSuccess(`File "${selectedFile.name}" selected`);
    } else {
      setFile(null);
      setFileError("Please upload only Excel files (.xls or .xlsx)");
      setFileSuccess("");
    }
  };
 
  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const response = await request(
        "GET",
        `/v1/kii/questionnaires/list?status=COMPLETED&offset=${page}&limit=${rowsPerPage}`,
        {},
        "kii_service"
      );
      setTemplates(
        response.content.map((item) => ({ name: item.name, value: item.id }))
      );
      setTotalData(response.totalElements);
    } catch (error) {
      console.error("Error fetching data:", error);
      handleApiError(error);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [page, rowsPerPage]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle file upload
  const handleFileUpload = async () => {
    if (!file) {
      setFileError("No file selected. Please choose a file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file, file.name);

    const bodyParam = encodeURIComponent(
      JSON.stringify({
        source: "KOBO_TOOL_BOX",
        organization: 1,
      })
    );

    try {
      const response = await request(
        "POST",
        `/v1/kii/questionnaires/upload?body=${bodyParam}`,
        {
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
        "kii_service"
      );

      if (response) {
        showToast({ message: "File Uploaded successfully", type: "success" });
        setTimeout(() => {
          navigate("/view-kii-questionnaire", {
            state: {
              id: response.id,
              viewType: "write",
            },
          });
        });

        setFileSuccess("File uploaded successfully!");
        setFileError("");
      } else {
        setFileError(response.message);
      }
    } catch (error) {
      setFileError(error?.response?.data?.message);
      handleApiError(error);
    }
  };

  useEffect(() => {
    navigate(".", {
      state: {
        activeTab,
      },
      replace: true,
    });
  }, [activeTab]);

  // Render Tab Content
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Select Template Tab
        return (
          <div>
            <TemplateCardView
              templates={templates}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
            />
            <div className="flex justify-center mt-6">
              <PaginationRound
                rowsPerPageOptions={[10, 25, 100]}
                count={totalData / rowsPerPage}
                rowsPerPage={rowsPerPage}
                page={page + 1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
            <div className="flex justify-center mt-6">
              <Link
                to={selectedTemplate ?  "/create-kii-questionnaire":""}
                state={selectedTemplate ?{
                  name: templates?.find(
                    (item) => item?.value === selectedTemplate
                  )?.name,
                  id: templates?.find((item) => item?.value === selectedTemplate)
                    ?.value,
                  isTemplate: true,
                }:{}}
                style={{ textDecoration: "none" }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!selectedTemplate}
                >
                  Proceed with Selected Template
                </Button>
              </Link>
            </div>
          </div>
        );
      case 1: // Create New Tab
        return (
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="body1" gutterBottom>
                Create a new questionnaire from scratch.
              </Typography>
              <Link to="/create-kii-questionnaire">
                <Button variant="contained" color="primary">
                  Create New Questionnaire
                </Button>
              </Link>
            </CardContent>
          </Card>
        );
      case 2: // File Import Tab
        return (
          <Card>
            <CardContent>
              <div
                {...getRootProps()}
                style={{
                  border: "2px dashed #ccc",
                  borderRadius: "4px",
                  padding: "20px",
                  textAlign: "center",
                  cursor: "pointer",
                }}
              >
                <input {...getInputProps()} />
                <Typography variant="body1" className="p-2">
                  {isDragActive
                    ? "Drop the file here"
                    : "Drag 'n' drop an Excel file here, or click to select a file"}
                </Typography>
              </div>
              {fileSuccess && (
                <Typography color="success">{fileSuccess}</Typography>
              )}
              {fileError && <Typography color="error">{fileError}</Typography>}
              <div style={{ marginTop: "20px", textAlign: "center" }} className={`${loading ? 'flex justify-center gap-3' : ''}`}>
                {loading && <CircularProgress size={30}/>}
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!file || loading}
                  onClick={handleFileUpload}
                  className={`${loading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  Upload and Proceed
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <Common headline={"Create KII Questionnaire"}>
      <div>
        <Tabs value={activeTab} onChange={handleTabChange} centered>
          <Tab label="Select Template" />
          <Tab label="Create New" />
          <Tab label="File Import" />
        </Tabs>
        <div style={{ marginTop: "24px" }}>{renderTabContent()}</div>
      </div>
    </Common>
  );
};

export default SelectQuestionnaireMethod;
